import React, { useState, useEffect } from "react";
import { Drawer, Typography, Space, Button, Row, Col, Form, Select } from "antd";
import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax"; // Import minMax plugin
import metaData from "../../data/metaData";
import { showErrMsg } from "../../utils/error";
import log from "../../utils/log";
import c from "../../utils/common";
import PeriodPicker from "../General/PeriodPicker";
import Number from "../General/Number";
import SectionHeader from "../GetStarted/SectionHeader";
import { formatTime, isWithin } from "../../utils/time";
import moment from "moment";
import useCompanyStore from "../../states/useCompany";

const { Title, Text } = Typography;
dayjs.extend(minMax); // Extend dayjs with the minMax plugin

const GiftDetailDrawer = ({
  open,
  gift,
  product,
  gifts = [],
  selectedPeriod,
  header,
  closable = true,
  isLoading = false,
  confirmBtnText,
  onClose,
  onConfirm,
}) => {
  const { getMaxShopExpiry } = useCompanyStore();
  const [form] = Form.useForm();
  const [localGift, setLocalGift] = useState(gift);
  const [warningMessage, setWarningMessage] = useState(""); // New state for warning message


  log.debug(localGift);
  log.debug(product);

  const isOngoing = localGift?.time?.redeem ? isWithin(localGift.time.redeem) : false;
  const validateForm = async (form) => {
    try {
      await form.validateFields();
    } catch (error) {
      log.error(error);
      c.message({
        type: "error",
        content: "Some of the input fields are invalid. Please review and save again",
      });
      throw new Error("Some of the input fields are invalid. Please review and save again");
    }
  };

  const submit = async () => {
    try {
      // Validate the form
      await validateForm(form);
      onConfirm({ ...localGift, periodId: selectedPeriod?.id });
    } catch (error) {
      log.debug(error);
      showErrMsg(error);
    }
  };

  // Update local state when gift prop changes
  useEffect(() => {
    if (gift) {
      setLocalGift(gift);
    }
  }, [gift]);

  // Function to update local gift state
  const updateLocalGiftData = (updatedData) => {
    setLocalGift((prevGift) => ({
      ...prevGift,
      ...updatedData,
    }));
  };

  return (
    <Drawer
      title={header}
      open={open}
      width="50%"
      onClose={() => {
        setWarningMessage();
        onClose();
      }}
      closable={closable}
      extra={
        <Button
          type="primary"
          size="large"
          loading={isLoading}
          disabled={!localGift || !product || warningMessage} // Disable if there's a warning
          onClick={submit}
        >
          {confirmBtnText}
        </Button>
      }
    >
      {!localGift || !product ? null : (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <img
            style={{
              width: 350,
              objectFit: "cover",
              borderRadius: "0.5rem",
              border: "1px solid var(--ion-color-step-200)",
            }}
            src={metaData.blobPreURL + "clientUpload-" + product.gallery[0] + ".jpeg" + metaData.blobPostURL}
          />
          <SectionHeader title={product.name} description={`price $ ${product.price}`} style={{ margin: 0 }} />

          <Space direction="vertical" style={{ width: "100%" }}>
            <InfoItem
              label="Selected Coin"
              value={
                selectedPeriod ? (
                  <Space direction="vertical">
                    <SectionHeader
                      style={{ margin: 0 }}
                      title={
                        <Title style={{ margin: 0 }} level={5}>
                          {selectedPeriod.name}
                        </Title>
                      }
                      description={`$${selectedPeriod?.rate} Earn 1 Coin`}
                    />
                    <Text type="secondary">{`${formatTime(
                      selectedPeriod?.time?.earn?.start,
                      "YYYY-MM-DD"
                    )} to ${formatTime(selectedPeriod?.time?.expiry, "YYYY-MM-DD")}`}</Text>
                  </Space>
                ) : null
              }
            />

            <InfoItem label="Available Qty" value={(product?.qty?.available || 0) + (gift?.qty || 0)} />
            <InfoItem
              label="Qty"
              value={
                <Number
                  state={{
                    data: localGift?.qty,
                    form: form,
                    rules: [
                      { required: true, message: "required" },
                      {
                        validator: (_, value) =>
                          value > 0 ? Promise.resolve() : Promise.reject(new Error("Must be greater than zero")),
                      },
                    ],
                    onChange: (value) => value > 0 && updateLocalGiftData({ qty: value }),
                  }}
                  min={1}
                  max={(product?.qty?.available || 0) + (gift?.qty || 0)}
                  keyName="qty"
                  readOnly={false}
                />
              }
            />
            <InfoItem
              label="Required Coin to redeem"
              value={
                isOngoing ? (
                  <Text strong>{localGift?.cost}</Text>
                ) : (
                  <Number
                    state={{
                      data: localGift?.cost,
                      form: form,
                      rules: [
                        { required: true, message: "required" },
                        {
                          validator: (_, value) =>
                            value > 0 ? Promise.resolve() : Promise.reject(new Error("Must be greater than zero")),
                        },
                      ],
                      onChange: (value) => value > 0 && updateLocalGiftData({ cost: value }),
                    }}
                    min={1}
                    keyName="cost"
                    readOnly={false}
                  />
                )
              }
            />
            <InfoItem
              label="Redeem Period"
              value={
                isOngoing ? (
                  <Text strong>
                    {`${formatTime(localGift.time.redeem.start, "YYYY-MM-DD")} to ${formatTime(
                      localGift.time.redeem.end,
                      "YYYY-MM-DD"
                    )}`}
                  </Text>
                ) : (
                  <>
                    <PeriodPicker
                      state={{
                        data: localGift?.time?.redeem,
                        form: form,
                        rules: [{ required: true, message: "required" }],
                        onChange: (updated) => {
                          updateLocalGiftData({
                            time: { ...localGift.time, redeem: updated },
                          });
                        },
                        keyName: "redeemPeriod",
                      }}
                      minDate={dayjs.max(dayjs(selectedPeriod?.time?.earn?.start), dayjs())}
                      maxDate={
                        selectedPeriod?.time?.expiry && product?.time?.redeem?.end
                          ? dayjs.min(
                              dayjs(selectedPeriod.time.expiry),
                              dayjs(product.time.redeem.end),
                              getMaxShopExpiry()
                            )
                          : getMaxShopExpiry()
                      }
                      occupiedDates={gifts
                        .filter((gift) => gift.productId === product.id)
                        .map((gift) => ({
                          start: gift.time.redeem.start,
                          end: gift.time.redeem.end,
                        }))}
                      setWarningMessage={(message) => {
                        if (message == "Overlapped invalid date") {
                          setWarningMessage(
                            "This gift is already redeemable on the selected dates. Please select again"
                          );
                        }
                      }}
                    />
                    {warningMessage && (
                      <Text type="danger">{warningMessage}</Text> // Display warning message
                    )}
                  </>
                )
              }
            />
          </Space>
        </Space>
      )}
    </Drawer>
  );
};

const InfoItem = ({ label, value, icon }) => (
  <Row justify="start" align="middle" style={{ marginBottom: "1rem" }}>
    <Col span={8}>
      <Space>
        {icon}
        <Text strong>{label}:</Text>
      </Space>
    </Col>
    <Col span={16}>
      <Text strong>{value}</Text>
    </Col>
  </Row>
);

export default GiftDetailDrawer;
